import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { INDUSTRIES } from "../../lib/data";

export function SectorSelector() {
  const [active, setActive] = useState(INDUSTRIES[0].slug);
  const sector = INDUSTRIES.find((s) => s.slug === active) || INDUSTRIES[0];

  return (
    <div className="mt-10">
      {/* Sector navigation bar */}
      <div
        data-testid="sector-nav"
        className="flex gap-2.5 overflow-x-auto pb-2 lg:flex-wrap lg:overflow-visible scrollbar-none"
      >
        {INDUSTRIES.map((s) => {
          const on = s.slug === active;
          return (
            <button
              key={s.slug}
              type="button"
              data-testid={`sector-pill-${s.slug}`}
              onClick={() => setActive(s.slug)}
              aria-pressed={on}
              className={`whitespace-nowrap px-4 py-2.5 text-sm font-semibold border transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flame focus-visible:ring-offset-2 ${
                on
                  ? "bg-flame text-white border-flame"
                  : "bg-transparent text-ink border-border hover:border-flame/60 hover:text-flame"
              }`}
            >
              {s.name}
            </button>
          );
        })}
      </div>

      {/* Selected sector preview */}
      <div
        data-testid="sector-preview"
        className="mt-8 grid overflow-hidden border border-border bg-card lg:grid-cols-2"
      >
        <div className="relative min-h-[260px] lg:min-h-[380px] overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.img
              key={sector.slug}
              src={sector.image}
              alt={sector.name}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent lg:bg-gradient-to-r" />
        </div>

        <div className="p-8 lg:p-10 flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={sector.slug}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.25, 1, 0.5, 1] }}
            >
              <div className="kicker text-flame">Sector</div>
              <h3 className="mt-2 font-display font-bold text-3xl text-ink">{sector.name}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{sector.blurb}</p>
              <div className="mt-6 space-y-2.5">
                {sector.useCases.map((u) => (
                  <div key={u} className="flex items-center gap-2.5 text-sm text-ink">
                    <Check className="h-4 w-4 text-flame flex-shrink-0" />
                    {u}
                  </div>
                ))}
              </div>
              <Link
                to={`/industries/${sector.slug}`}
                data-testid={`sector-explore-${sector.slug}`}
                className="mt-8 inline-flex items-center gap-2 self-start bg-ink text-white px-6 py-3 font-semibold hover:bg-flame transition-colors"
              >
                Explore {sector.name} <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default SectorSelector;
